import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  SzButtonComponent,
  SzChipRowComponent,
  SzChipRowOption,
  SzIconComponent,
  SzNumpadGridComponent,
  SzNumpadKey
} from '@ui';

const NUMPAD_KEYS: ReadonlyArray<SzNumpadKey> = [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  { label: '9', value: '9' },
  { label: 'C', value: 'clear', variant: 'fn' },
  { label: '0', value: '0' },
  { label: '⌫', value: 'backspace', variant: 'icon' }
];

type PresetKey = 'preset-5' | 'preset-10' | 'preset-15' | 'custom' | 'no-target';

@Component({
  selector: 'lib-target-runs-form',
  templateUrl: './target-runs-form.component.html',
  styleUrls: ['./target-runs-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, SzChipRowComponent, SzNumpadGridComponent, SzButtonComponent, SzIconComponent]
})
export class TargetRunsFormComponent {
  readonly submitLabel: InputSignal<string> = input.required<string>();
  readonly initialValue: InputSignal<number | null> = input<number | null>(null);
  readonly targetSubmit: OutputEmitterRef<number | null> = output<number | null>();

  readonly numpadKeys: ReadonlyArray<SzNumpadKey> = NUMPAD_KEYS;

  readonly selectedTarget: WritableSignal<number | null> = signal<number | null>(null);
  readonly customBuffer: WritableSignal<string> = signal<string>('');
  readonly customMode: WritableSignal<boolean> = signal<boolean>(false);
  readonly noTargetSelected: WritableSignal<boolean> = signal<boolean>(false);
  readonly activePreset: WritableSignal<PresetKey | null> = signal<PresetKey | null>(null);

  readonly chipOptions: ReadonlyArray<SzChipRowOption<PresetKey>> = [
    { label: '5', value: 'preset-5' },
    { label: '10', value: 'preset-10' },
    { label: '15', value: 'preset-15' },
    { label: 'Custom', value: 'custom' },
    { label: 'No target', value: 'no-target' }
  ];

  constructor() {
    this.applyInitialValue();
  }

  onChipSelect(key: PresetKey): void {
    this.activePreset.set(key);
    if (key === 'preset-5') this.applyPreset(5);
    else if (key === 'preset-10') this.applyPreset(10);
    else if (key === 'preset-15') this.applyPreset(15);
    else if (key === 'no-target') this.applyNoTarget();
    else this.enableCustomMode();
  }

  onNumpadPress(value: string): void {
    if (!this.customMode()) {
      this.enableCustomMode();
    }
    if (value === 'clear') {
      this.customBuffer.set('');
    } else if (value === 'backspace') {
      this.customBuffer.update((s: string) => s.slice(0, -1));
    } else {
      this.customBuffer.update((s: string) => (s + value).slice(0, 4));
    }
    const parsed: number = Number.parseInt(this.customBuffer(), 10);
    this.selectedTarget.set(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
  }

  submit(): void {
    this.targetSubmit.emit(this.selectedTarget());
  }

  isSubmitDisabled(): boolean {
    return !this.noTargetSelected() && this.selectedTarget() === null;
  }

  private applyPreset(target: number): void {
    this.customMode.set(false);
    this.noTargetSelected.set(false);
    this.selectedTarget.set(target);
    this.customBuffer.set('');
  }

  private applyNoTarget(): void {
    this.customMode.set(false);
    this.noTargetSelected.set(true);
    this.selectedTarget.set(null);
    this.customBuffer.set('');
  }

  private enableCustomMode(): void {
    this.customMode.set(true);
    this.noTargetSelected.set(false);
  }

  private applyInitialValue(): void {
    effect(() => {
      const value: number | null = this.initialValue();
      if (value === null) {
        this.activePreset.set('no-target');
        this.noTargetSelected.set(true);
        return;
      }
      if (value === 5) {
        this.activePreset.set('preset-5');
        this.applyPreset(5);
      } else if (value === 10) {
        this.activePreset.set('preset-10');
        this.applyPreset(10);
      } else if (value === 15) {
        this.activePreset.set('preset-15');
        this.applyPreset(15);
      } else {
        this.activePreset.set('custom');
        this.customMode.set(true);
        this.customBuffer.set(String(value));
        this.selectedTarget.set(value);
      }
    });
  }
}
